import { createRoot } from 'react-dom/client';
import React from 'react';
import SearchableSelect from '../components/common/SearchableSelect';

class AutoSearchableSelectsManager {
  constructor() {
    this.processedSelects = new WeakSet();
    this.roots = new Map();
    this.observer = null;
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.processExistingSelects();

    this.observeDOM();
  }

  processExistingSelects() {
    const selects = document.querySelectorAll('select');
    selects.forEach(select => this.processSelect(select));
  }

  processSelect(select) {
    if (this.processedSelects.has(select)) return;
    
    if (select.hasAttribute('data-no-search')) return;
    
    if (!select.offsetParent && select.style.display === 'none') return;

    this.processedSelects.add(select);

    const selectClasses = select.className;
    const isDisabled = select.disabled;
    const parentElement = select.parentElement;

    const originalDisplay = select.style.display;
    const originalPosition = select.style.position;
    
    select.style.position = 'absolute';
    select.style.opacity = '0';
    select.style.pointerEvents = 'none';
    select.style.height = '0';
    select.style.width = '0';
    select.style.zIndex = '-1';
    select.setAttribute('data-searchable-hidden', 'true');

    const wrapper = document.createElement('div');
    wrapper.className = 'searchable-select-wrapper';
    wrapper.style.display = originalDisplay || 'block';
    
    parentElement.insertBefore(wrapper, select);

    const root = createRoot(wrapper);
    
    root.render(
      React.createElement(SearchableSelect, {
        selectElement: select,
        disabled: isDisabled,
        className: selectClasses
      })
    );

    this.roots.set(select, { root, wrapper, originalPosition, originalDisplay });

    const attrObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'disabled') {
          root.render(
            React.createElement(SearchableSelect, {
              selectElement: select,
              disabled: select.disabled,
              className: selectClasses
            })
          );
        }
      });
    });

    attrObserver.observe(select, { attributes: true });
  }

  observeDOM() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { 
            if (node.tagName === 'SELECT') {
              setTimeout(() => this.processSelect(node), 0);
            }
            if (node.querySelectorAll) {
              const selects = node.querySelectorAll('select');
              selects.forEach(select => {
                setTimeout(() => this.processSelect(select), 0);
              });
            }
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.tagName === 'SELECT') {
            this.cleanupSelect(node);
          }
        });
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  cleanupSelect(select) {
    const data = this.roots.get(select);
    if (data) {
      data.root.unmount();
      data.wrapper.remove();
      this.roots.delete(select);
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.roots.forEach((data, select) => {
      data.root.unmount();
      data.wrapper.remove();
      
      select.style.position = data.originalPosition || '';
      select.style.opacity = '';
      select.style.pointerEvents = '';
      select.style.height = '';
      select.style.width = '';
      select.style.zIndex = '';
      select.style.display = data.originalDisplay || '';
      select.removeAttribute('data-searchable-hidden');
    });

    this.roots.clear();
    this.processedSelects = new WeakSet();
    this.initialized = false;

    console.log('❌ AutoSearchableSelects desativado.');
  }
}

const manager = new AutoSearchableSelectsManager();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => manager.init());
} else {
  manager.init();
}

export default manager;